import { Photo } from '@/types/photo';
import { PhotoCard } from '../PhotoCard';
import { PhotoContextMenu } from '@/components/PhotoContextMenu';
import { LoadingState, ErrorState } from '@/components/composed';
import { Text } from '@/components/atoms';
import { useAuth } from '@/services/auth';

export interface PhotoGridProps {
  photos: Photo[];
  userSlug: string;
  isLoading?: boolean;
  error?: Error | null;
  onPhotoDelete?: (photoId: string) => void;
  deletingPhotoId?: string | null;
  onRetry?: () => void;
  className?: string;
}

export function PhotoGrid({
  photos,
  userSlug,
  isLoading = false,
  error = null,
  onPhotoDelete,
  deletingPhotoId = null,
  onRetry,
  className = '',
}: PhotoGridProps) {
  // Feature 081: photo-versioning - Get current user for ownership check
  const { user } = useAuth();
  if (isLoading) {
    return <LoadingState message="Loading photos..." size="lg" />;
  }

  if (error) {
    return (
      <ErrorState
        title="Failed to load photos"
        message={error.message || 'An error occurred while loading photos. Please try again.'}
        onRetry={onRetry}
      />
    );
  }

  if (photos.length === 0) {
    return (
      <div className="text-center py-15 px-5 bg-muted/20 rounded-lg my-5">
        <Text variant="body" className="text-lg mb-2">No photos yet</Text>
        <Text variant="muted" className="text-sm">Upload your first photo to get started!</Text>
      </div>
    );
  }

  return (
    <div className={`columns-1 md:columns-2 lg:columns-3 gap-4 py-5 ${className}`}>
      {photos.map((photo) => {
        // Feature 081: photo-versioning - Check if current user owns this photo
        const isOwner = user?.id ? photo.userId === user.id : false;

        return (
          <div key={photo.id} className="mb-4 break-inside-avoid">
            <PhotoContextMenu
              photoId={photo.id}
              isOwner={isOwner}
            >
              <PhotoCard
                photo={photo}
                userSlug={userSlug}
                onDelete={onPhotoDelete}
                isDeleting={deletingPhotoId === photo.id}
              />
            </PhotoContextMenu>
          </div>
        );
      })}
    </div>
  );
}
